/**
 * Transcripts API layer. The Django API wraps every body in the standard
 * envelope (`{ success, message, data }`); `apiFetch` returns it verbatim, so
 * this layer unwraps `data` and maps snake_case↔camelCase.
 *
 * Money is integer **cents** on both sides — the serializer emits `*_cents`
 * fields (camelized to `*Cents` here); no dollar conversion happens in this layer.
 */

import { ApiError, apiFetch, apiFetchBlob } from "@/lib/api";
import type { CsedResult } from "@/features/transcripts/lib/csed";
import type {
  BankruptcyRow,
  FtaResult,
  ForWho,
  PaymentRecord,
  TaxRecord,
  TaxRecordDetail,
  TaxRecordInput,
  WaidItem,
} from "@/features/transcripts/types";

interface Envelope<T> {
  data: T;
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const toCamel = (s: string) => s.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
const toSnake = (s: string) => s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

/** Deep key-rename; array/scalar values pass through unchanged. */
function rename<T>(value: unknown, fn: (k: string) => string): T {
  if (Array.isArray(value)) return value.map((v) => rename(v, fn)) as T;
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[fn(k)] = rename(v, fn);
    return out as T;
  }
  return value as T;
}

const camelize = <T>(v: unknown): T => rename<T>(v, toCamel);
const decamelize = (v: unknown): unknown => rename(v, toSnake);

const base = (clientId: string) => `/clients/${clientId}`;

/** Build a `?a=b&c=d` string from defined, non-empty params. */
function query(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

// --- tax records -------------------------------------------------------------

export interface TaxRecordFilters {
  forWho?: ForWho;
  hideZero?: boolean;
  hideUnfiled?: boolean;
}

export async function listTaxRecords(
  clientId: string,
  filters: TaxRecordFilters = {},
): Promise<TaxRecord[]> {
  const q = query({
    for_who: filters.forWho,
    hide_zero: filters.hideZero || undefined,
    hide_unfiled: filters.hideUnfiled || undefined,
    // The page computes footer totals + sorts over the whole set, so pull the
    // full (bounded, per-client) list rather than the default 25-row page.
    page_size: 100,
  });
  const res = await apiFetch<Envelope<unknown[]>>(`${base(clientId)}/tax-records/${q}`);
  return res.data.map((row) => camelize<TaxRecord>(row));
}

export async function getTaxRecord(clientId: string, id: string): Promise<TaxRecordDetail> {
  const res = await apiFetch<Envelope<unknown>>(`${base(clientId)}/tax-records/${id}/`);
  return camelize<TaxRecordDetail>(res.data);
}

export async function getCsed(clientId: string, id: string): Promise<CsedResult> {
  const res = await apiFetch<Envelope<unknown>>(`${base(clientId)}/tax-records/${id}/csed/`);
  return camelize<CsedResult>(res.data);
}

export async function createTaxRecord(
  clientId: string,
  payload: TaxRecordInput,
): Promise<TaxRecordDetail> {
  const res = await apiFetch<Envelope<unknown>>(`${base(clientId)}/tax-records/`, {
    method: "POST",
    json: decamelize(payload),
  });
  return camelize<TaxRecordDetail>(res.data);
}

export async function updateTaxRecord(
  clientId: string,
  id: string,
  payload: Partial<TaxRecordInput>,
): Promise<TaxRecordDetail> {
  const res = await apiFetch<Envelope<unknown>>(`${base(clientId)}/tax-records/${id}/`, {
    method: "PATCH",
    json: decamelize(payload),
  });
  return camelize<TaxRecordDetail>(res.data);
}

export async function deleteTaxRecord(clientId: string, id: string): Promise<void> {
  await apiFetch<void>(`${base(clientId)}/tax-records/${id}/`, { method: "DELETE" });
}

export async function bulkDeleteTaxRecords(clientId: string, ids: string[]): Promise<number> {
  const res = await apiFetch<Envelope<{ deleted: number }>>(
    `${base(clientId)}/tax-records/bulk-delete/`,
    { method: "POST", json: { ids } },
  );
  return res.data.deleted;
}

// --- computed lists ----------------------------------------------------------

export async function listPayments(
  clientId: string,
  filters: { forWho?: ForWho; form?: string; taxYear?: number; paymentYear?: number } = {},
): Promise<PaymentRecord[]> {
  const q = query({
    for_who: filters.forWho,
    form: filters.form,
    tax_year: filters.taxYear,
    payment_year: filters.paymentYear,
  });
  const res = await apiFetch<Envelope<unknown[]>>(`${base(clientId)}/payments/${q}`);
  return res.data.map((row) => camelize<PaymentRecord>(row));
}

export async function listWaidItems(
  clientId: string,
  filters: { forWho?: ForWho; taxYear?: number } = {},
): Promise<WaidItem[]> {
  const q = query({ for_who: filters.forWho, tax_year: filters.taxYear });
  const res = await apiFetch<Envelope<unknown[]>>(`${base(clientId)}/waid-items/${q}`);
  return res.data.map((row) => camelize<WaidItem>(row));
}

export async function listFta(clientId: string, forWho?: ForWho): Promise<FtaResult> {
  const res = await apiFetch<Envelope<unknown>>(`${base(clientId)}/fta/${query({ for_who: forWho })}`);
  return camelize<FtaResult>(res.data);
}

/** Trigger a browser download of the per-client FTA Excel workbook. */
export async function exportFtaXlsx(clientId: string, forWho: ForWho): Promise<void> {
  const blob = await apiFetchBlob(`${base(clientId)}/fta/export/${query({ for_who: forWho })}`);
  triggerDownload(blob, "fta.xlsx");
}

/** Open the per-client FTA PDF in a new tab (branded with the firm letterhead).
 * Falls back to a download when the browser blocks the popup (the open happens
 * after an `await`, so it isn't always treated as a user gesture). */
export async function printFtaPdf(clientId: string, forWho: ForWho): Promise<void> {
  const blob = await apiFetchBlob(`${base(clientId)}/fta/pdf/${query({ for_who: forWho })}`);
  const url = URL.createObjectURL(blob);
  // Not "noopener": that makes window.open always return null, so we couldn't
  // detect a blocked popup. A blob: PDF can't script window.opener anyway.
  const win = window.open(url, "_blank");
  if (!win) {
    triggerDownload(blob, "fta.pdf");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export interface FtaArchiveInput {
  docName: string;
  folderId?: string;
  overwrite?: boolean;
  forWho: ForWho;
}

export interface FtaArchiveResult {
  id: string;
  name: string;
  version: number;
}

/** Save the FTA PDF to the client's Documents. Resolves `{ exists: true }` when
 * a same-named document already exists and `overwrite` was not set (409), so the
 * caller can prompt to confirm the overwrite. */
export async function archiveFta(
  clientId: string,
  input: FtaArchiveInput,
): Promise<FtaArchiveResult | { exists: true }> {
  try {
    const res = await apiFetch<Envelope<FtaArchiveResult>>(`${base(clientId)}/fta/archive/`, {
      method: "POST",
      json: decamelize({
        docName: input.docName,
        folderId: input.folderId,
        overwrite: input.overwrite ?? false,
        forWho: input.forWho,
      }),
    });
    return res.data;
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      return { exists: true };
    }
    throw err;
  }
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function listBankruptcy(clientId: string, forWho?: ForWho): Promise<BankruptcyRow[]> {
  const res = await apiFetch<Envelope<unknown[]>>(
    `${base(clientId)}/bankruptcy/${query({ for_who: forWho })}`,
  );
  return res.data.map((row) => camelize<BankruptcyRow>(row));
}

// --- upload ------------------------------------------------------------------

export interface UploadSummary {
  type: string;
  created: number;
  updated: number;
  skipped: number;
}

export async function uploadTranscript(
  clientId: string,
  file: File,
  forWho: ForWho,
): Promise<UploadSummary> {
  const form = new FormData();
  form.append("file", file);
  form.append("for_who", forWho);
  const res = await apiFetch<Envelope<UploadSummary>>(
    `${base(clientId)}/transcripts/upload/`,
    { method: "POST", form },
  );
  return res.data;
}
